import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useRef, useState } from 'react';

const defaultFormatCurrency = (amount = 0) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);

const formatReadableDate = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// A4 dimensions in pixels (at 96 DPI)
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MM_TO_PX = 3.779527559; // 1mm = 3.779527559px at 96 DPI
const A4_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX; // ~794px
const A4_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX; // ~1123px
const PAGE_MARGIN = 5; // 10mm margin (reduced from 20mm for more content space)
const CONTENT_HEIGHT_PX = A4_HEIGHT_PX - (PAGE_MARGIN * 2 * MM_TO_PX);

const PurchaseOrderPrint = ({ purchaseOrder, isOpen, onClose, formatCurrency }) => {
  const scrollRef = useRef(null);
  const printableRefs = useRef([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [estimatedPages, setEstimatedPages] = useState(1);
  const [pageContent, setPageContent] = useState([]);
  const formatMoney = formatCurrency || defaultFormatCurrency;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const items = purchaseOrder?.items || [];
  const billingAddress = purchaseOrder?.billingAddress || {};
  const shippingAddress = purchaseOrder?.shippingAddress || {};
  const poInfo = purchaseOrder?.poInfo || {};
  const selectedBillingSite = purchaseOrder?.selectedBillingSite || '';
  const selectedShippingSite = purchaseOrder?.selectedShippingSite || '';
  const sites = purchaseOrder?.sites || [];
  const selectedVendor = purchaseOrder?.selectedVendor || '';

  // Initialize pageContent when component opens
  useEffect(() => {
    if (!isOpen) {
      setPageContent([]);
      printableRefs.current = [];
    }
  }, [isOpen]);

  // Calculate page content distribution
  useEffect(() => {
    if (isOpen && purchaseOrder && items.length >= 0) {
      // Calculate how many items fit on first page
      // Estimate: header (~150px) + vendor info (~80px) + billing (~120px) + shipping (if exists ~120px) + table header (~40px) + footer (~40px)
      const firstPageFixedHeight = 150 + 80 + 120 + (!purchaseOrder.useSameAddress ? 120 : 0) + 40 + 40;
      const availableHeightFirstPage = CONTENT_HEIGHT_PX - firstPageFixedHeight;
      const availableHeightOtherPages = CONTENT_HEIGHT_PX - 40; // Just footer on other pages
      
      // Estimate item row height (~35px per row)
      const itemRowHeight = 35;
      const itemsPerFirstPage = Math.max(0, Math.floor(availableHeightFirstPage / itemRowHeight));
      const itemsPerOtherPage = Math.max(0, Math.floor(availableHeightOtherPages / itemRowHeight));
      
      const totalItems = items.length;
      const pages = [];
      
      // If no items or all items fit on first page
      if (totalItems === 0 || totalItems <= itemsPerFirstPage) {
        // All items fit on first page
        pages.push({
          pageNumber: 1,
          items: items,
          showHeader: true,
          showVendorInfo: true,
          showBillingAddress: true,
          showShippingAddress: !purchaseOrder.useSameAddress,
          showTotals: true,
        });
        setEstimatedPages(1);
      } else {
        // First page with header and some items
        const firstPageItems = items.slice(0, itemsPerFirstPage);
        pages.push({
          pageNumber: 1,
          items: firstPageItems,
          showHeader: true,
          showVendorInfo: true,
          showBillingAddress: true,
          showShippingAddress: !purchaseOrder.useSameAddress,
          showTotals: false,
        });
        
        // Remaining items
        let remainingItems = items.slice(itemsPerFirstPage);
        let pageNum = 2;
        
        while (remainingItems.length > 0) {
          const pageItems = remainingItems.slice(0, itemsPerOtherPage);
          remainingItems = remainingItems.slice(itemsPerOtherPage);
          
          pages.push({
            pageNumber: pageNum,
            items: pageItems,
            showHeader: false,
            showVendorInfo: false,
            showBillingAddress: false,
            showShippingAddress: false,
            showTotals: remainingItems.length === 0, // Show totals on last page
            isContinuation: true,
          });
          
          pageNum++;
        }
        
        setEstimatedPages(pages.length);
      }
      
      setPageContent(pages);
    }
  }, [isOpen, purchaseOrder, items.length]);

  if (!isOpen || !purchaseOrder) {
    return null;
  }

  const handleDownloadPdf = async () => {
    if (!printableRefs.current || printableRefs.current.length === 0 || isGeneratingPdf) {
      return;
    }
    try {
      setIsGeneratingPdf(true);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - margin * 2;

      // Process each page
      for (let i = 0; i < printableRefs.current.length; i++) {
        const pageRef = printableRefs.current[i];
        if (!pageRef) continue;

        if (i > 0) {
          pdf.addPage();
        }

        const canvas = await html2canvas(pageRef, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        });

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = availableWidth / imgWidth;
        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;

        const offsetX = (pageWidth - scaledWidth) / 2;
        const offsetY = margin;

        pdf.addImage(
          canvas.toDataURL('image/png', 1.0),
          'PNG',
          offsetX,
          offsetY,
          scaledWidth,
          scaledHeight,
          undefined,
          'FAST'
        );
      }

      pdf.save(`${purchaseOrder.poNumber || 'purchase-order'}.pdf`);
    } catch (error) {
      console.error('Failed to generate purchase order PDF', error);
      alert('Unable to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const getSiteName = (siteId) => {
    const site = sites.find(
      (s) =>
        String(s?.site_id || s?.siteId || s?.id || s?._id || s?.site_code || s?.siteCode) ===
        String(siteId)
    );
    return site?.site_name || site?.name || site?.siteCode || site?.site_code || '—';
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-800">
      {/* Print Styles */}
      <style>{`
        @media print {
          .pdf-viewer-toolbar,
          .pdf-viewer-controls {
            display: none !important;
          }
          .pdf-page {
            page-break-after: always;
            margin: 0 !important;
            box-shadow: none !important;
            break-after: page;
          }
          .pdf-viewer-container {
            background: white !important;
            padding: 0 !important;
          }
        }
      `}</style>

      {/* PDF Viewer Toolbar */}
      <div className="pdf-viewer-toolbar flex-shrink-0 bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-semibold" style={{ color: "#fff" }}>
              Purchase Order Preview - PDF Viewer
            </h2>
            <div className="h-5 w-px bg-gray-600"></div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <span className="font-medium">Estimated Pages:</span>
              <span className="px-2 py-1 bg-gray-700 rounded text-white font-semibold">
                {estimatedPages} {estimatedPages === 1 ? 'page' : 'pages'}
              </span>
            </div>
            <div className="h-5 w-px bg-gray-600"></div>
            <div className="text-xs text-gray-400">
              A4 Size (210mm × 297mm)
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isGeneratingPdf ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Generating...
                </>
              ) : (
                <>
                  <span>📥</span>
                  Download PDF
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* PDF Viewer Container */}
      <div className="pdf-viewer-container flex-1 overflow-auto bg-gray-600 p-8" ref={scrollRef}>
        <div className="flex flex-col items-center gap-6 min-h-full py-8">
          {pageContent.length > 0 ? (
            pageContent.map((page, pageIndex) => {
              // Calculate starting item number for continuation pages
              const startItemNumber = pageIndex === 0 
                ? 1 
                : pageContent.slice(0, pageIndex).reduce((sum, p) => sum + p.items.length, 0) + 1;

              return (
                <div
                  key={page.pageNumber}
                  ref={(el) => {
                    if (el) printableRefs.current[pageIndex] = el;
                  }}
                  className="pdf-page bg-white shadow-2xl"
                  style={{
                    width: `${A4_WIDTH_PX}px`,
                    minHeight: `${A4_HEIGHT_PX}px`,
                    padding: `${PAGE_MARGIN * MM_TO_PX}px`,
                    boxSizing: 'border-box',
                  }}
                >
                  <div className="flex flex-col gap-4" style={{ minHeight: '100%' }}>
                    {/* Header - only on first page */}
                    {page.showHeader && (
                      <section className="flex flex-col gap-3 border-b border-gray-300 pb-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h1 className="text-3xl font-bold tracking-wide text-gray-900">PURCHASE ORDER</h1>
                          <p className="mt-1 text-base font-semibold text-gray-600">
                            {purchaseOrder.poNumber || 'PO-' + Date.now().toString().slice(-6)}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="space-y-0.5 text-xs font-medium text-gray-700">
                            <p>
                              PO Date:{' '}
                              <span className="font-semibold text-gray-900">
                                {formatReadableDate(poInfo.poDate) || '—'}
                              </span>
                            </p>
                            {poInfo.poExpiryDate && (
                              <p>
                                PO Expiry:{' '}
                                <span className="font-semibold text-gray-900">
                                  {formatReadableDate(poInfo.poExpiryDate)}
                                </span>
                              </p>
                            )}
                            <p>
                              Expected Delivery:{' '}
                              <span className="font-semibold text-gray-900">
                                {formatReadableDate(poInfo.expectedDeliveryDate) || '—'}
                              </span>
                            </p>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Continuation header for subsequent pages */}
                    {page.isContinuation && (
                      <section className="flex flex-col gap-2 border-b border-gray-300 pb-2">
                        <h2 className="text-lg font-semibold text-gray-700">Purchase Order (Continued)</h2>
                        <p className="text-xs text-gray-600">
                          {purchaseOrder.poNumber || 'PO-' + Date.now().toString().slice(-6)}
                        </p>
                      </section>
                    )}

                    {/* Vendor and Site Information - only on first page */}
                    {page.showVendorInfo && (
                      <section className="overflow-hidden border border-gray-300 rounded">
                        <table className="w-full text-xs text-gray-700">
                          <tbody>
                            <tr className="border-b border-gray-300">
                              <td className="w-28 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                Vendor
                              </td>
                              <td className="px-3 py-2 text-gray-900">{selectedVendor || '—'}</td>
                              <td className="w-28 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                Billing Site
                              </td>
                              <td className="px-3 py-2 text-gray-900">{getSiteName(selectedBillingSite)}</td>
                            </tr>
                            <tr className="border-b border-gray-300">
                              <td className="w-28 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                Payment Mode
                              </td>
                              <td className="px-3 py-2 text-gray-900">{poInfo.paymentMode || '—'}</td>
                              <td className="w-28 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                Shipping Site
                              </td>
                              <td className="px-3 py-2 text-gray-900">{getSiteName(selectedShippingSite)}</td>
                            </tr>
                            {poInfo.paymentDate && (
                              <tr>
                                <td className="w-28 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
                                  Payment Date
                                </td>
                                <td className="px-3 py-2 text-gray-900">
                                  {formatReadableDate(poInfo.paymentDate)}
                                </td>
                                <td className="w-28 bg-gray-100 px-3 py-2"></td>
                                <td className="px-3 py-2"></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </section>
                    )}

                    {/* Billing Address - only on first page */}
                    {page.showBillingAddress && (
                      <section className="border border-gray-300 rounded p-3">
                        <p className="text-sm font-semibold uppercase tracking-wide text-gray-800">
                          Billing Address
                        </p>
                        <div className="mt-2 space-y-0.5 text-xs text-gray-700">
                          <p className="font-semibold text-gray-900">
                            {billingAddress.firstName} {billingAddress.lastName}
                          </p>
                          <p>{billingAddress.email}</p>
                          <p>{billingAddress.address}</p>
                          <p>
                            {billingAddress.city}, {billingAddress.state}
                          </p>
                          <p>
                            {billingAddress.country} - {billingAddress.pinCode}
                          </p>
                          <p className="mt-2">Mobile: {billingAddress.mobileNumber}</p>
                          <p>PAN: {billingAddress.panNumber}</p>
                          <p>GST: {billingAddress.gstNumber}</p>
                        </div>
                      </section>
                    )}

                    {/* Shipping Address - only on first page */}
                    {page.showShippingAddress && (
                      <section className="border border-gray-300 rounded p-3">
                        <p className="text-sm font-semibold uppercase tracking-wide text-gray-800">
                          Shipping Address
                        </p>
                        <div className="mt-2 space-y-0.5 text-xs text-gray-700">
                          <p className="font-semibold text-gray-900">
                            {shippingAddress.firstName} {shippingAddress.lastName}
                          </p>
                          <p>{shippingAddress.email}</p>
                          <p>{shippingAddress.address}</p>
                          <p>
                            {shippingAddress.city}, {shippingAddress.state}
                          </p>
                          <p>
                            {shippingAddress.country} - {shippingAddress.pinCode}
                          </p>
                          <p className="mt-2">Mobile: {shippingAddress.mobileNumber}</p>
                          <p>PAN: {shippingAddress.panNumber}</p>
                          <p>GST: {shippingAddress.gstNumber}</p>
                        </div>
                      </section>
                    )}

                    {/* Items Table */}
                    <section className="overflow-hidden border border-gray-300 rounded">
                      <table className="w-full border-collapse text-xs">
                        <thead>
                          <tr className="bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-700">
                            <th className="px-2 py-2 text-left">S.No</th>
                            <th className="px-2 py-2 text-left">Name</th>
                            <th className="px-2 py-2 text-left">HSN Code</th>
                            <th className="px-2 py-2 text-center">Unit</th>
                            <th className="px-2 py-2 text-center">Quantity</th>
                            <th className="px-2 py-2 text-right">Rate</th>
                            <th className="px-2 py-2 text-right">Tax (%)</th>
                            <th className="px-2 py-2 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {page.items.length ? (
                            page.items.map((item, itemIndex) => {
                              const units = parseFloat(item.units) || 0;
                              const rate = parseFloat(item.rate) || 0;
                              const tax = parseFloat(item.tax) || 0;
                              const base = units * rate;
                              const taxValue = base * (tax / 100);
                              const amount = base + taxValue;
                              const itemNumber = startItemNumber + itemIndex;

                              return (
                                <tr
                                  key={`item-${page.pageNumber}-${itemIndex}`}
                                  className="border-b border-gray-300 last:border-b-0 text-gray-800"
                                >
                                  <td className="px-2 py-2 text-xs font-semibold text-gray-600">
                                    {itemNumber}
                                  </td>
                                  <td className="px-2 py-2">
                                    <p className="font-semibold text-gray-900 text-xs">{item.name || '—'}</p>
                                  </td>
                                  <td className="px-2 py-2 text-gray-700 text-xs">{item.hsnCode || '—'}</td>
                                  <td className="px-2 py-2 text-center text-gray-700 text-xs">{item.unit || '—'}</td>
                                  <td className="px-2 py-2 text-center text-gray-700 text-xs">{units}</td>
                                  <td className="px-2 py-2 text-right text-gray-700 text-xs">
                                    {formatMoney(rate)}
                                  </td>
                                  <td className="px-2 py-2 text-right text-gray-700 text-xs">{tax}%</td>
                                  <td className="px-2 py-2 text-right font-semibold text-gray-900 text-xs">
                                    {formatMoney(amount)}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={8} className="px-3 py-4 text-center text-xs text-gray-500">
                                No items added yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {page.showTotals && (
                          <tfoot className="text-xs font-semibold text-gray-900">
                            <tr className="border-t border-gray-300">
                              <td colSpan={7} className="px-2 py-2 text-right">Subtotal</td>
                              <td className="px-2 py-2 text-right">
                                {formatMoney(purchaseOrder.subtotal || 0)}
                              </td>
                            </tr>
                            <tr className="border-t border-gray-300">
                              <td colSpan={7} className="px-2 py-2 text-right">Discount</td>
                              <td className="px-2 py-2 text-right">
                                {formatMoney(purchaseOrder.discountValue || 0)}
                              </td>
                            </tr>
                            <tr className="border-t border-gray-300 bg-gray-100">
                              <td colSpan={7} className="px-2 py-2 text-right font-bold uppercase text-sm">
                                Grand Total
                              </td>
                              <td className="px-2 py-2 text-right font-bold text-gray-900 text-sm">
                                {formatMoney(purchaseOrder.grandTotal || 0)}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </section>

                    {/* Page Footer */}
                    <div className="mt-auto pt-2 border-t border-gray-200 text-center text-xs text-gray-500">
                      Page {page.pageNumber} of {estimatedPages}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="pdf-page bg-white shadow-2xl p-8" style={{ width: `${A4_WIDTH_PX}px`, minHeight: `${A4_HEIGHT_PX}px` }}>
              <div className="text-center text-gray-500">Loading preview...</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderPrint;

